import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useAutoRefresh from '../hooks/useAutoRefresh';
import useWindowWidth from '../hooks/useWindowWidth';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
    ArrowLeft, LayoutDashboard, FolderKanban, Users, FileText,
    Calendar, TrendingUp, Mail, Plus, Trash2, ChevronLeft, ChevronRight, Pencil,
    FileSignature, Upload, Download, X, ClipboardList
} from 'lucide-react';
import {
    ResponsiveContainer, AreaChart, Area, BarChart, Bar,
    XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import { SENDER_EMPTY, loadSender, saveSender, buildInvoiceHTML, wireInvoiceWindow } from '../utils/invoiceTemplate';
import { LEISTUNGEN, EMPTY_CONTRACT_DATA, buildContractHTML, wireContractWindow, loadContractDefaults, saveContractDefaults } from '../utils/contractTemplate';
import { EMPTY_OFFER_DATA, emptyOfferPos, buildOfferHTML, wireOfferWindow } from '../utils/offerTemplate';

const API = process.env.REACT_APP_API_URL;
const ACCENT = '#fb923c';
const MUTED = '#a0a0a0';
const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const DAYS_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const inputStyle = {
    background: 'var(--bg-secondary)', border: '2px solid var(--border)',
    borderRadius: '10px', color: 'var(--text-primary)', fontFamily: 'inherit',
    fontSize: '15px', padding: '12px 16px', width: '100%', boxSizing: 'border-box'
};
const selectStyle = { ...inputStyle, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' };

const STATUS_COLORS = {
    offen: '#f59e0b', bezahlt: '#4ade80', gesendet: '#60a5fa', storniert: '#ef4444',
    aktiv: '#4ade80', archiv: '#f59e0b', abgeschlossen: '#a0a0a0',
    entwurf: '#f59e0b', unterschrieben: '#4ade80',
    angenommen: '#4ade80', abgelehnt: '#ef4444',
    Neu: '#60a5fa', Kontaktiert: '#f59e0b', Angebot: '#a78bfa', Gewonnen: '#4ade80', Verloren: '#ef4444',
    Hoch: '#ef4444', Mittel: '#f59e0b', Niedrig: '#4ade80',
};

function Badge({ status }) {
    const c = STATUS_COLORS[status] || '#a0a0a0';
    return <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', background: `${c}22`, color: c, border: `1px solid ${c}44`, whiteSpace: 'nowrap' }}>{status}</span>;
}

function StatCard({ label, value, sub, color = ACCENT }) {
    return (
        <div className="project-card" style={{ padding: '20px 24px', cursor: 'default' }}>
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '4px', fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: '26px', fontWeight: 800, color }}>{value}</div>
            {sub && <div style={{ fontSize: '12px', color: MUTED, marginTop: '4px' }}>{sub}</div>}
        </div>
    );
}

function CancelBtn({ onClick }) {
    return (
        <button type="button" onClick={onClick} className="icon-button"
            style={{ border: '2px solid var(--border)', borderRadius: '10px', padding: '11px 22px', fontSize: '15px', fontWeight: 600, color: 'var(--text-secondary)' }}>
            Abbrechen
        </button>
    );
}

const getInitials = (name) => {
    if (!name || !name.trim()) return 'X';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return parts.map(p => p[0]).join('').toUpperCase();
};

// ─── SIDEBAR ───────────────────────────────────────────────────────────────────
function Sidebar({ view, setView, isMobile }) {
    const items = [
        { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { key: 'projects', label: 'Projekte', icon: FolderKanban },
        { key: 'clients', label: 'Kunden', icon: Users },
        { key: 'invoices', label: 'Rechnungen', icon: FileText },
        { key: 'offers', label: 'Angebote', icon: ClipboardList },
        { key: 'contracts', label: 'Verträge', icon: FileSignature },
        { key: 'calendar', label: 'Kalender', icon: Calendar },
        { key: 'financials', label: 'Finanzen', icon: TrendingUp },
    ];

    if (isMobile) {
        return (
            <div style={{
                width: '100%', flexShrink: 0,
                background: 'var(--bg-primary, #0d0d0d)',
                borderBottom: '1px solid var(--border)',
                display: 'flex', overflowX: 'auto', gap: '4px',
                padding: '8px 12px',
            }}>
                <Link to="/privat" className="icon-button"
                    style={{ display: 'flex', alignItems: 'center', padding: '8px 10px', color: 'var(--text-secondary)', textDecoration: 'none', borderRadius: '8px', flexShrink: 0 }}>
                    <ArrowLeft size={16} />
                </Link>
                {items.map(({ key, label, icon: Icon }) => (
                    <button key={key} onClick={() => setView(key)} style={{
                        display: 'flex', alignItems: 'center', gap: '5px',
                        padding: '8px 12px', borderRadius: '8px', border: 'none',
                        background: view === key ? `${ACCENT}18` : 'transparent',
                        color: view === key ? ACCENT : MUTED,
                        fontWeight: view === key ? 700 : 500,
                        fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap',
                        flexShrink: 0, fontFamily: 'inherit'
                    }}>
                        <Icon size={14} /> {label}
                    </button>
                ))}
            </div>
        );
    }

    return (
        <div style={{
            width: '220px', flexShrink: 0,
            background: 'var(--bg-primary, #0d0d0d)',
            borderRight: '1px solid var(--border)',
            padding: '24px 12px',
            display: 'flex', flexDirection: 'column', gap: '4px',
            position: 'sticky', top: '70px',
            height: 'calc(100vh - 70px)', overflowY: 'auto'
        }}>
            <Link to="/privat" className="icon-button"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', padding: '8px 12px', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', textDecoration: 'none', borderRadius: '8px' }}>
                <ArrowLeft size={15} /> Privater Bereich
            </Link>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#444', padding: '0 12px', marginBottom: '8px', letterSpacing: '0.12em' }}>NAVIGATION</div>
            {items.map(({ key, label, icon: Icon }) => (
                <button key={key} onClick={() => setView(key)} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 14px', borderRadius: '8px', border: 'none',
                    background: view === key ? `${ACCENT}18` : 'transparent',
                    color: view === key ? ACCENT : MUTED,
                    fontWeight: view === key ? 700 : 500,
                    fontSize: '14px', cursor: 'pointer', width: '100%', textAlign: 'left',
                    transition: 'all 0.15s', fontFamily: 'inherit'
                }}>
                    <Icon size={16} /> {label}
                </button>
            ))}
        </div>
    );
}

// ─── DASHBOARD ─────────────────────────────────────────────────────────────────
function Dashboard({ clients, projects, invoices, setView, isMobile }) {
    const now = new Date();
    const paid = useMemo(() => invoices.filter(i => i.status === 'bezahlt'), [invoices]);
    const pending = useMemo(() => invoices.filter(i => i.status !== 'bezahlt' && i.status !== 'storniert'), [invoices]);
    const totalRevenue = paid.reduce((s, i) => s + i.amount, 0);
    const totalPending = pending.reduce((s, i) => s + i.amount, 0);
    const activeProjects = projects.filter(p => p.status === 'aktiv');

    const monthlyData = useMemo(() => Array.from({ length: 6 }, (_, idx) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (5 - idx), 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return {
            label: d.toLocaleDateString('de-DE', { month: 'short' }),
            amount: paid.filter(inv => (inv.created_at || '').startsWith(key)).reduce((s, inv) => s + inv.amount, 0)
        };
    }), [paid]);

    return (
        <div>
            <h1 className="section-title" style={{ marginBottom: '8px' }}>Dashboard</h1>
            <p className="section-subtitle" style={{ marginBottom: '32px' }}>Willkommen zurück im Business</p>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
                <StatCard label="Gesamtumsatz" value={`€${totalRevenue.toFixed(0)}`} sub="Bezahlte Rechnungen" />
                <StatCard label="Ausstehend" value={`€${totalPending.toFixed(0)}`} sub={`${pending.length} offene Rechnungen`} color="#f59e0b" />
                <StatCard label="Aktive Projekte" value={activeProjects.length} sub={`${projects.length} gesamt`} color="#60a5fa" />
                <StatCard label="Kunden" value={clients.length} sub={`${paid.length} bezahlte Rechnungen`} color="#4ade80" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px' }}>
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px' }}>
                    <h2 className="project-title" style={{ marginBottom: '20px' }}>Umsatzentwicklung</h2>
                    <ResponsiveContainer width="100%" height={180}>
                        <AreaChart data={monthlyData}>
                            <defs>
                                <linearGradient id="esg" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={ACCENT} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={ACCENT} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                            <XAxis dataKey="label" tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px' }} formatter={v => [`€${v}`, 'Umsatz']} />
                            <Area type="monotone" dataKey="amount" stroke={ACCENT} fill="url(#esg)" strokeWidth={2} dot={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <span className="project-title">Aktuelle Projekte</span>
                            <button onClick={() => setView('projects')} className="icon-button" style={{ color: ACCENT, fontSize: '13px', fontWeight: 600 }}>Alle →</button>
                        </div>
                        {activeProjects.length === 0 && <p className="project-description">Keine aktiven Projekte</p>}
                        {activeProjects.slice(0, 3).map(p => (
                            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                                <span style={{ fontSize: '14px', fontWeight: 600 }}>{p.title}</span>
                                <span style={{ fontSize: '13px', color: ACCENT, fontWeight: 700 }}>€{p.price}</span>
                            </div>
                        ))}
                    </div>
                    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <span className="project-title">Letzte Rechnungen</span>
                            <button onClick={() => setView('invoices')} className="icon-button" style={{ color: ACCENT, fontSize: '13px', fontWeight: 600 }}>Alle →</button>
                        </div>
                        {invoices.length === 0 && <p className="project-description">Keine Rechnungen</p>}
                        {invoices.slice(0, 3).map(inv => (
                            <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                                <div>
                                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{inv.number}</div>
                                    <div style={{ fontSize: '11px', color: MUTED }}>{inv.due_date || '—'}</div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontWeight: 700, color: ACCENT }}>€{inv.amount}</span>
                                    <Badge status={inv.status} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── PROJECTS ──────────────────────────────────────────────────────────────────
const EMPTY_PROJECT = { title: '', client_id: '', price: '', date_from: '', date_to: '', platform: '', content_type: '', videos_total: '', notes: '' };

function ProjectsView({ token, projects, clients, onRefresh, isMobile }) {
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_PROJECT);
    const [dragOver, setDragOver] = useState(null);
    const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

    const openNew = () => { setEditingId(null); setForm(EMPTY_PROJECT); setShowForm(true); };
    const openEdit = (p) => {
        setEditingId(p.id);
        setForm({
            title: p.title || '', client_id: p.client_id ? String(p.client_id) : '', price: p.price ? String(p.price) : '',
            date_from: p.date_from || '', date_to: p.date_to || '', platform: p.platform || '',
            content_type: p.content_type || '', videos_total: p.videos_total ? String(p.videos_total) : '', notes: p.notes || '',
        });
        setShowForm(true);
    };
    const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_PROJECT); };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.title) return;
        const body = { ...form, price: parseFloat((form.price || '0').replace(',', '.')) || 0, client_id: parseInt(form.client_id) || 0, videos_total: parseInt(form.videos_total) || 0 };
        await fetch(`${API}/api/essentials/projects${editingId ? `/${editingId}` : ''}`, {
            method: editingId ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body)
        });
        closeForm();
        onRefresh();
    };

    const setStatus = async (id, status) => {
        await fetch(`${API}/api/essentials/projects/${id}/status`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ status })
        });
        onRefresh();
    };

    const updateTasks = async (id, current, delta) => {
        await fetch(`${API}/api/essentials/projects/${id}/videos`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ videos_done: Math.max(0, current + delta) })
        });
        onRefresh();
    };

    const del = async (id, title) => {
        if (!window.confirm(`Projekt "${title}" wirklich löschen? Das kann nicht rückgängig gemacht werden.`)) return;
        await fetch(`${API}/api/essentials/projects/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        onRefresh();
    };

    const clientName = id => clients.find(c => c.id === id)?.name || '—';
    const columns = [
        { key: 'aktiv', label: 'Aktiv', color: '#4ade80' },
        { key: 'archiv', label: 'In Review', color: '#f59e0b' },
        { key: 'abgeschlossen', label: 'Abgeschlossen', color: MUTED },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
                <div>
                    <h1 className="section-title" style={{ marginBottom: '4px' }}>Projekte</h1>
                    <p className="section-subtitle">Entwicklungs- & Kundenprojekte verwalten</p>
                </div>
                <button onClick={() => (showForm ? closeForm() : openNew())} className="btn-primary"><Plus size={16} /> Neues Projekt</button>
            </div>

            {showForm && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px', marginBottom: '28px' }}>
                    <h3 className="project-title" style={{ marginBottom: '20px' }}>{editingId ? 'Projekt bearbeiten' : 'Neues Projekt'}</h3>
                    <form onSubmit={handleSave} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                        <div><label className="form-label">Titel *</label><input value={form.title} onChange={f('title')} style={inputStyle} placeholder="Projektname" /></div>
                        <div><label className="form-label">Budget (€)</label><input value={form.price} onChange={f('price')} style={inputStyle} placeholder="0" /></div>
                        <div>
                            <label className="form-label">Auftraggeber</label>
                            <select value={form.client_id} onChange={f('client_id')} style={selectStyle}>
                                <option value="">— Kein Auftraggeber —</option>
                                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div><label className="form-label">Tech-Stack</label><input value={form.platform} onChange={f('platform')} style={inputStyle} placeholder="z.B. React, Node.js, Docker" /></div>
                        <div><label className="form-label">Start</label><input type="text" value={form.date_from} onChange={f('date_from')} style={inputStyle} placeholder="TT.MM.JJJJ" maxLength={10} /></div>
                        <div><label className="form-label">Deadline</label><input type="text" value={form.date_to} onChange={f('date_to')} style={inputStyle} placeholder="TT.MM.JJJJ" maxLength={10} /></div>
                        <div><label className="form-label">Projekttyp</label><input value={form.content_type} onChange={f('content_type')} style={inputStyle} placeholder="z.B. Web App, API, CLI, Mobile" /></div>
                        <div><label className="form-label">Tasks gesamt</label><input value={form.videos_total} onChange={f('videos_total')} style={inputStyle} placeholder="0" /></div>
                        <div style={{ gridColumn: '1 / -1' }}><label className="form-label">Notizen <span style={{ color: MUTED, fontWeight: 400 }}>(Optional)</span></label><input value={form.notes} onChange={f('notes')} style={inputStyle} placeholder="z.B. Repo-Link, Anforderungen..." /></div>
                        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px' }}>
                            <button type="submit" className="btn-primary">Speichern</button>
                            <CancelBtn onClick={closeForm} />
                        </div>
                    </form>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '24px' }}>
                {columns.map(col => (
                    <div key={col.key}
                        onDragOver={e => { e.preventDefault(); setDragOver(col.key); }}
                        onDragLeave={() => setDragOver(null)}
                        onDrop={e => {
                            e.preventDefault();
                            const id = parseInt(e.dataTransfer.getData('projectId'));
                            if (id) setStatus(id, col.key);
                            setDragOver(null);
                        }}
                        style={{ borderRadius: '12px', transition: 'background 0.15s', background: dragOver === col.key ? `${col.color}0f` : 'transparent', padding: '4px' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: col.color }} />
                            <span style={{ fontWeight: 700 }}>{col.label}</span>
                            <span style={{ fontSize: '12px', color: MUTED }}>({projects.filter(p => p.status === col.key).length})</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {projects.filter(p => p.status === col.key).map(p => (
                                <div key={p.id}
                                    draggable
                                    onDragStart={e => { e.dataTransfer.setData('projectId', p.id); e.dataTransfer.effectAllowed = 'move'; }}
                                    style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '18px', cursor: 'grab' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                        <span className="project-title" style={{ fontSize: '15px' }}>{p.title}</span>
                                        <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                                            <button onClick={() => openEdit(p)} className="icon-button" style={{ color: '#555', padding: '0 0 0 6px' }}><Pencil size={14} /></button>
                                            <button onClick={() => del(p.id, p.title)} className="icon-button" style={{ color: '#555', padding: '0 0 0 6px' }}><Trash2 size={14} /></button>
                                        </div>
                                    </div>
                                    {p.client_id > 0 && <div className="project-description" style={{ marginBottom: '4px' }}>👤 {clientName(p.client_id)}</div>}
                                    {(p.platform || p.content_type) && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                                            {p.platform && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: `${ACCENT}18`, color: ACCENT, fontWeight: 600 }}>{p.platform}</span>}
                                            {p.content_type && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: 'var(--bg-secondary)', color: MUTED, fontWeight: 600, border: '1px solid var(--border)' }}>{p.content_type}</span>}
                                        </div>
                                    )}
                                    {p.price > 0 && <div style={{ fontSize: '15px', color: ACCENT, fontWeight: 700, marginBottom: '8px' }}>€{p.price}</div>}
                                    {p.videos_total > 0 && (
                                        <div style={{ marginBottom: '10px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                                <span className="project-description">Tasks: {p.videos_done}/{p.videos_total}</span>
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    <button onClick={() => updateTasks(p.id, p.videos_done, -1)} className="icon-button" style={{ width: '22px', height: '22px', fontSize: '14px', justifyContent: 'center', border: '1px solid var(--border)', borderRadius: '4px' }}>−</button>
                                                    <button onClick={() => updateTasks(p.id, p.videos_done, 1)} className="icon-button" style={{ width: '22px', height: '22px', fontSize: '14px', justifyContent: 'center', border: '1px solid var(--border)', borderRadius: '4px' }}>+</button>
                                                </div>
                                            </div>
                                            <div style={{ height: '3px', background: 'var(--border)', borderRadius: '2px' }}>
                                                <div style={{ height: '100%', width: `${Math.min(100, (p.videos_done / p.videos_total) * 100)}%`, background: col.color, borderRadius: '2px', transition: 'width 0.3s' }} />
                                            </div>
                                        </div>
                                    )}
                                    {(p.date_from || p.date_to) && <div style={{ fontSize: '11px', color: '#666', marginBottom: '10px' }}>📅 {p.date_from} – {p.date_to}</div>}
                                    {p.notes && <div style={{ fontSize: '12px', color: MUTED, marginBottom: '8px', fontStyle: 'italic' }}>{p.notes}</div>}
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        {columns.filter(c => c.key !== col.key).map(c => (
                                            <button key={c.key} onClick={() => setStatus(p.id, c.key)} style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '6px', cursor: 'pointer', background: 'var(--bg-secondary)', border: '1px solid var(--border)', color: c.color, fontWeight: 700, fontFamily: 'inherit' }}>→ {c.label}</button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {projects.filter(p => p.status === col.key).length === 0 && (
                                <div style={{ color: MUTED, fontSize: '13px', textAlign: 'center', padding: '28px', background: 'var(--card-bg)', border: `1px dashed ${dragOver === col.key ? col.color : 'var(--border)'}`, borderRadius: '12px' }}>
                                    {dragOver === col.key ? '📥 Hier ablegen' : 'Keine Projekte'}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── CLIENTS ───────────────────────────────────────────────────────────────────
const EMPTY_CLIENT = { name: '', company: '', email: '', phone: '' };

function ClientsView({ token, clients, onRefresh, isMobile }) {
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_CLIENT);
    const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

    const openNew = () => { setEditingId(null); setForm(EMPTY_CLIENT); setShowForm(true); };
    const openEdit = (c) => {
        setEditingId(c.id);
        setForm({ name: c.name || '', company: c.company || '', email: c.email || '', phone: c.phone || '' });
        setShowForm(true);
    };
    const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_CLIENT); };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name) return;
        if (editingId) {
            const existing = clients.find(c => c.id === editingId);
            await fetch(`${API}/api/essentials/clients/${editingId}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ ...form, number: existing?.number || '' }),
            });
        } else {
            await fetch(`${API}/api/essentials/clients`, {
                method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify((() => {
                    const initials = getInitials(form.name);
                    const prefix = `KN-${initials}-`;
                    const existingNums = clients.map(c => parseInt((c.number || '').replace(prefix, '')) || 0).filter(Boolean);
                    const nextN = String(Math.max(0, ...existingNums) + 1).padStart(3, '0');
                    return { ...form, number: `${prefix}${nextN}` };
                })())
            });
        }
        closeForm();
        onRefresh();
    };

    const del = async (id, name) => {
        if (!window.confirm(`Kunde "${name}" wirklich löschen? Das kann nicht rückgängig gemacht werden.`)) return;
        await fetch(`${API}/api/essentials/clients/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        onRefresh();
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
                <div>
                    <h1 className="section-title" style={{ marginBottom: '4px' }}>Kunden</h1>
                    <p className="section-subtitle">Verwalte deine Kundeninformationen</p>
                </div>
                <button onClick={() => (showForm ? closeForm() : openNew())} className="btn-primary"><Plus size={16} /> Neuer Kunde</button>
            </div>

            {showForm && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px', marginBottom: '28px' }}>
                    <h3 className="project-title" style={{ marginBottom: '20px' }}>{editingId ? 'Kunde bearbeiten' : 'Neuer Kunde'}</h3>
                    <form onSubmit={handleSave} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                        {[['name', 'Name *', 'Kundenname'], ['company', 'Firma', 'Firmenname'], ['email', 'E-Mail', 'email@example.com'], ['phone', 'Telefon', '+49...']].map(([k, label, ph]) => (
                            <div key={k}><label className="form-label">{label}</label><input value={form[k]} onChange={f(k)} style={inputStyle} placeholder={ph} /></div>
                        ))}
                        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px' }}>
                            <button type="submit" className="btn-primary">Speichern</button>
                            <CancelBtn onClick={closeForm} />
                        </div>
                    </form>
                </div>
            )}

            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            {['Kd-Nr.', 'Name', 'Firma', 'E-Mail', 'Telefon', ''].map(h => (
                                <th key={h} style={{ padding: '14px 18px', textAlign: 'left', fontSize: '12px', fontWeight: 700, color: MUTED }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {clients.map(c => (
                            <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ padding: '14px 18px', fontSize: '12px', color: MUTED }}>{c.number}</td>
                                <td style={{ padding: '14px 18px', fontSize: '15px', fontWeight: 600 }}>{c.name}</td>
                                <td style={{ padding: '14px 18px', fontSize: '14px', color: MUTED }}>{c.company || '—'}</td>
                                <td style={{ padding: '14px 18px', fontSize: '14px', color: MUTED }}>{c.email || '—'}</td>
                                <td style={{ padding: '14px 18px', fontSize: '14px', color: MUTED }}>{c.phone || '—'}</td>
                                <td style={{ padding: '14px 18px', display: 'flex', gap: '4px' }}>
                                    <button onClick={() => openEdit(c)} className="icon-button" style={{ color: MUTED }}><Pencil size={15} /></button>
                                    <button onClick={() => del(c.id, c.name)} className="icon-button" style={{ color: '#ef4444' }}><Trash2 size={15} /></button>
                                </td>
                            </tr>
                        ))}
                        {clients.length === 0 && <tr><td colSpan={6} style={{ padding: '48px', textAlign: 'center', color: MUTED }}>Noch keine Kunden</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── INVOICES ──────────────────────────────────────────────────────────────────
const emptyPos = () => ({ beschreibung: '', menge: 1, preis: '' });

const PENDING_RECHNER_KEY = 'pending_rechnung_data';

function InvoicesView({ token, invoices, projects, clients, rechnungen, onRefresh, isMobile }) {
    const now = new Date();
    const [view, setView] = useState('list'); // 'list' | 'new'
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        project_id: '', client_id: '', kundenname: '', adresse: '',
        datum: now.toLocaleDateString('de-DE'), due_date: '',
        date_from: '', date_to: '', mwst: 0,
        positionen: [emptyPos()],
    });
    const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

    // Übernahme aus dem Rechner (Aufwand & Angebot)
    useEffect(() => {
        try {
            const raw = localStorage.getItem(PENDING_RECHNER_KEY);
            if (!raw) return;
            const data = JSON.parse(raw);
            localStorage.removeItem(PENDING_RECHNER_KEY);
            if (!data?.positionen?.length) return;
            setForm(p => ({
                ...p,
                positionen: data.positionen.map(pos => ({
                    beschreibung: pos.beschreibung || '',
                    menge: pos.menge ?? 1,
                    preis: typeof pos.preis === 'number' ? pos.preis.toFixed(2) : (pos.preis || ''),
                })),
                mwst: data.mwst ?? p.mwst,
            }));
            setView('new');
        } catch { /* ignore */ }
    }, []);

    // ── Absenderdaten ──────────────────────────────────────────────────────────
    const [sender, setSender] = useState(loadSender);
    const [senderOpen, setSenderOpen] = useState(() => {
        const s = loadSender();
        return !s.name; // offen wenn noch nicht ausgefüllt
    });
    const [senderSaved, setSenderSaved] = useState(false);
    const fs = k => e => setSender(p => ({ ...p, [k]: e.target.value }));
    const handleSaveSender = () => {
        saveSender(sender);
        setSenderSaved(true);
        setTimeout(() => setSenderSaved(false), 2000);
        setSenderOpen(false);
    };

    const nextNumber = useMemo(() => {
        const initials = getInitials(form.kundenname);
        const prefix = `${initials}-`;
        const parseNum = s => parseInt((s || '').replace(/^[A-Z]+-0*/, '')) || 0;
        const allNums = [
            ...invoices.map(i => i.number || ''),
            ...rechnungen.map(r => r.nummer || ''),
        ].filter(n => n.startsWith(prefix)).map(parseNum);
        const maxNum = Math.max(0, ...allNums);
        return `${initials}-${String(maxNum + 1).padStart(3, '0')}`;
    }, [invoices, rechnungen, form.kundenname]);

    const netto = form.positionen.reduce((s, p) => s + (parseFloat(p.preis) || 0) * (parseFloat(p.menge) || 0), 0);
    const mwstBetrag = netto * (form.mwst / 100);
    const gesamt = netto + mwstBetrag;

    const updatePos = (i, field, val) => setForm(f => ({ ...f, positionen: f.positionen.map((p, idx) => idx === i ? { ...p, [field]: val } : p) }));
    const addPos = () => setForm(f => ({ ...f, positionen: [...f.positionen, emptyPos()] }));
    const removePos = i => setForm(f => ({ ...f, positionen: f.positionen.filter((_, idx) => idx !== i) }));

    const selectProject = e => {
        const proj = projects.find(p => String(p.id) === e.target.value);
        const client = proj ? clients.find(c => c.id === proj.client_id) : null;
        setForm(prev => ({
            ...prev,
            project_id: e.target.value,
            client_id: proj?.client_id ? String(proj.client_id) : prev.client_id,
            kundenname: client?.name || prev.kundenname,
            adresse: client?.company ? `${client.company}` : prev.adresse,
            date_from: proj?.date_from || prev.date_from,
            date_to: proj?.date_to || prev.date_to,
            positionen: proj ? [{ beschreibung: proj.title + (proj.content_type ? ` (${proj.content_type})` : ''), menge: 1, preis: String(proj.price || '') }] : prev.positionen,
        }));
    };

    const selectClient = e => {
        const client = clients.find(c => String(c.id) === e.target.value);
        setForm(prev => ({ ...prev, client_id: e.target.value, kundenname: client?.name || prev.kundenname, adresse: client?.company || prev.adresse }));
    };

    const handleSave = async () => {
        if (!form.kundenname) return;
        setSaving(true);
        // 1. In Essentials speichern
        await fetch(`${API}/api/essentials/invoices`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
                number: nextNumber,
                project_id: parseInt(form.project_id) || 0,
                client_id: parseInt(form.client_id) || 0,
                date_from: form.date_from, date_to: form.date_to,
                due_date: form.due_date, amount: gesamt, status: 'offen'
            })
        });
        // 2. Auch in Rechnungen speichern (sync)
        await fetch(`${API}/api/rechnungen`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
                nummer: nextNumber,
                kunde: form.kundenname + (form.adresse ? '\n' + form.adresse : ''),
                datum: form.datum,
                positionen: JSON.stringify(form.positionen),
                gesamt, status: 'offen'
            })
        });
        setSaving(false);
        // 3. PDF öffnen
        const html = buildInvoiceHTML({
            nummer: nextNumber, datum: form.datum, faellig: form.due_date,
            kundenname: form.kundenname, adresse: form.adresse,
            positionen: form.positionen, mwst: form.mwst,
            dateFrom: form.date_from, dateTo: form.date_to,
            sender,
        });
        const win = window.open('', '_blank');
        if (win) { win.document.write(html); win.document.close(); wireInvoiceWindow(win, nextNumber); }
        setForm({ project_id: '', client_id: '', kundenname: '', adresse: '', datum: now.toLocaleDateString('de-DE'), due_date: '', date_from: '', date_to: '', mwst: 0, positionen: [emptyPos()] });
        setView('list');
        onRefresh();
    };

    const setStatus = async (id, status) => {
        await fetch(`${API}/api/essentials/invoices/${id}/status`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ status })
        });
        onRefresh();
    };

    const del = async (id, number) => {
        if (!window.confirm(`Rechnung ${number || ''} wirklich löschen? Das kann nicht rückgängig gemacht werden.`)) return;
        await fetch(`${API}/api/essentials/invoices/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        onRefresh();
    };

    const repriceInput = { ...inputStyle, padding: '9px 12px', fontSize: '14px' };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
                <div>
                    <h1 className="section-title" style={{ marginBottom: '4px' }}>Rechnungen</h1>
                    <p className="section-subtitle">{view === 'new' ? `Neue Rechnung · ${nextNumber}` : 'Erstelle und verwalte deine Rechnungen'}</p>
                </div>
                <button onClick={() => setView(v => v === 'list' ? 'new' : 'list')} className="btn-primary">
                    {view === 'new' ? '← Zur Liste' : <><Plus size={16} /> Neue Rechnung</>}
                </button>
            </div>

            {view === 'new' && (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 380px', gap: '28px', alignItems: 'start' }}>
                    {/* ── FORM ── */}
                    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px', display: 'flex', flexDirection: 'column', gap: '18px' }}>

                        {/* ── Absenderdaten ── */}
                        <div style={{ background: 'var(--bg-secondary)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                            <button type="button" onClick={() => setSenderOpen(o => !o)}
                                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                                <span style={{ fontSize: '13px', fontWeight: 700, color: sender.name ? 'var(--text-primary)' : ACCENT }}>
                                    {sender.name ? `Absender: ${sender.name} · ${sender.iban || '—'}` : '⚠ Absenderdaten ausfüllen'}
                                </span>
                                <span style={{ fontSize: '12px', color: MUTED }}>{senderOpen ? '▲' : '▼'}</span>
                            </button>
                            {senderOpen && (
                                <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                                        <div><label className="form-label">Name *</label><input value={sender.name} onChange={fs('name')} style={inputStyle} placeholder="Dein Name" /></div>
                                        <div><label className="form-label">E-Mail</label><input value={sender.email} onChange={fs('email')} style={inputStyle} placeholder="email@example.com" /></div>
                                        <div><label className="form-label">Straße + Nr.</label><input value={sender.street} onChange={fs('street')} style={inputStyle} placeholder="Musterstraße 1" /></div>
                                        <div><label className="form-label">PLZ + Stadt</label><input value={sender.city} onChange={fs('city')} style={inputStyle} placeholder="12345 Musterstadt" /></div>
                                        <div><label className="form-label">IBAN</label><input value={sender.iban} onChange={fs('iban')} style={inputStyle} placeholder="DE00 0000 0000 ..." /></div>
                                        <div><label className="form-label">BIC</label><input value={sender.bic} onChange={fs('bic')} style={inputStyle} placeholder="BELADEBEXXX" /></div>
                                        <div><label className="form-label">Bank</label><input value={sender.bank} onChange={fs('bank')} style={inputStyle} placeholder="Sparkasse / Deutsche Bank ..." /></div>
                                        <div><label className="form-label">Steuernummer</label><input value={sender.taxNumber} onChange={fs('taxNumber')} style={inputStyle} placeholder="12/345/67890" /></div>
                                    </div>
                                    <button type="button" onClick={handleSaveSender} className="btn-primary" style={{ alignSelf: 'flex-start', padding: '10px 24px', fontSize: '14px' }}>
                                        {senderSaved ? '✓ Gespeichert' : '💾 Speichern & merken'}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Basis */}
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
                            <div>
                                <label className="form-label">Projekt (Optional)</label>
                                <select value={form.project_id} onChange={selectProject} style={selectStyle}>
                                    <option value="">— Kein Projekt —</option>
                                    {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="form-label">Kunde</label>
                                <select value={form.client_id} onChange={selectClient} style={selectStyle}>
                                    <option value="">— Kein Kunde —</option>
                                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
                            <div><label className="form-label">Kundenname *</label><input value={form.kundenname} onChange={f('kundenname')} style={inputStyle} placeholder="Max Mustermann" /></div>
                            <div><label className="form-label">Firma / Adresse</label><input value={form.adresse} onChange={f('adresse')} style={inputStyle} placeholder="Firma GmbH, Musterstr. 1" /></div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '14px' }}>
                            <div><label className="form-label">Datum</label><input value={form.datum} onChange={f('datum')} style={inputStyle} placeholder="TT.MM.JJJJ" maxLength={10} /></div>
                            <div><label className="form-label">Leistung von</label><input value={form.date_from} onChange={f('date_from')} style={inputStyle} placeholder="TT.MM.JJJJ" maxLength={10} /></div>
                            <div><label className="form-label">Leistung bis</label><input value={form.date_to} onChange={f('date_to')} style={inputStyle} placeholder="TT.MM.JJJJ" maxLength={10} /></div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
                            <div><label className="form-label">Fällig am</label><input value={form.due_date} onChange={f('due_date')} style={inputStyle} placeholder="TT.MM.JJJJ" maxLength={10} /></div>
                            <div>
                                <label className="form-label">MwSt.</label>
                                <select value={form.mwst} onChange={e => setForm(p => ({ ...p, mwst: Number(e.target.value) }))} style={selectStyle}>
                                    <option value={0}>0 % (Kleinunternehmer)</option>
                                    <option value={7}>7 %</option>
                                    <option value={19}>19 %</option>
                                </select>
                            </div>
                        </div>

                        {/* Positionen */}
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <label className="form-label" style={{ margin: 0 }}>Positionen</label>
                                <button type="button" onClick={addPos} className="icon-button" style={{ fontSize: '13px', padding: '5px 12px', border: '1px solid var(--border)', borderRadius: '6px' }}><Plus size={13} /> Position</button>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 28px', gap: '6px', fontSize: '11px', color: MUTED, padding: '0 4px', marginBottom: '4px' }}>
                                <span>Beschreibung</span><span style={{ textAlign: 'center' }}>Menge</span><span style={{ textAlign: 'right' }}>Preis €</span><span />
                            </div>
                            {form.positionen.map((p, i) => (
                                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 28px', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                                    <input value={p.beschreibung} onChange={e => updatePos(i, 'beschreibung', e.target.value)} style={repriceInput} placeholder="Leistungsbeschreibung" />
                                    <input value={p.menge} onChange={e => updatePos(i, 'menge', e.target.value)} style={{ ...repriceInput, textAlign: 'center' }} />
                                    <input value={p.preis} onChange={e => updatePos(i, 'preis', e.target.value.replace(',', '.'))} style={{ ...repriceInput, textAlign: 'right' }} placeholder="0.00" />
                                    {form.positionen.length > 1
                                        ? <button type="button" onClick={() => removePos(i)} className="icon-button" style={{ color: '#ef4444', padding: 0 }}><Trash2 size={14} /></button>
                                        : <div />}
                                </div>
                            ))}
                        </div>

                        {/* Summen */}
                        <div style={{ background: 'var(--bg-secondary)', borderRadius: '10px', padding: '14px 18px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: MUTED, marginBottom: '6px' }}><span>Netto</span><span>{netto.toFixed(2)} €</span></div>
                            {form.mwst > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: MUTED, marginBottom: '6px' }}><span>MwSt. {form.mwst}%</span><span>{mwstBetrag.toFixed(2)} €</span></div>}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '18px', borderTop: '1px solid var(--border)', paddingTop: '8px', color: ACCENT }}><span>Gesamt</span><span>{gesamt.toFixed(2)} €</span></div>
                        </div>

                        <button type="button" onClick={handleSave} className="btn-primary" disabled={saving || !form.kundenname} style={{ fontSize: '15px', padding: '14px' }}>
                            {saving ? 'Wird gespeichert…' : '💾 Speichern & PDF öffnen'}
                        </button>
                    </div>

                    {/* ── PREVIEW ── */}
                    <div style={{ background: '#fff', borderRadius: '12px', padding: '28px 24px', color: '#111', fontSize: '13px', boxShadow: '0 4px 32px rgba(0,0,0,0.35)', position: 'sticky', top: '80px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                            <div>
                                <div style={{ fontFamily: "Georgia,'Times New Roman',serif", fontSize: '19px', fontWeight: 700, color: '#1a1a1a', letterSpacing: '-0.3px' }}>&lt;MiS/&gt;</div>
                                {sender.name && <div style={{ fontSize: '10px', color: '#aaa', marginTop: '2px' }}>{sender.street && `${sender.street} · `}{sender.city}</div>}
                                {sender.email && <div style={{ fontSize: '10px', color: '#aaa' }}>{sender.email}</div>}
                                {sender.taxNumber && <div style={{ fontSize: '10px', color: '#aaa' }}>Steuernummer: {sender.taxNumber}</div>}
                            </div>
                            <div style={{ textAlign: 'right', fontSize: '11px', color: '#555', lineHeight: 1.8 }}>
                                <div><strong style={{ color: '#111' }}>{nextNumber}</strong></div>
                                <div>{form.datum || '—'}</div>
                                {form.due_date && <div>Fällig: {form.due_date}</div>}
                            </div>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '.1em', color: '#aaa', marginBottom: '4px' }}>Rechnung an</div>
                            <div style={{ fontWeight: 700, fontSize: '14px' }}>{form.kundenname || '—'}</div>
                            {form.adresse && <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{form.adresse}</div>}
                        </div>
                        {(form.date_from || form.date_to) && <div style={{ fontSize: '11px', color: '#999', marginBottom: '14px' }}>Leistungszeitraum: {form.date_from} – {form.date_to}</div>}
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px', fontSize: '12px' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    {['Beschreibung', 'Mge.', 'Preis', 'Gesamt'].map(h => <th key={h} style={{ padding: '6px 4px', textAlign: h === 'Beschreibung' ? 'left' : 'right', color: '#aaa', fontWeight: 600, fontSize: '10px', textTransform: 'uppercase' }}>{h}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {form.positionen.map((p, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '7px 4px', color: '#222' }}>{p.beschreibung || `Position ${i + 1}`}</td>
                                        <td style={{ padding: '7px 4px', textAlign: 'right', color: '#555' }}>{p.menge}</td>
                                        <td style={{ padding: '7px 4px', textAlign: 'right', color: '#555' }}>{(parseFloat(p.preis) || 0).toFixed(2)} €</td>
                                        <td style={{ padding: '7px 4px', textAlign: 'right', fontWeight: 600 }}>{((parseFloat(p.preis) || 0) * (parseFloat(p.menge) || 0)).toFixed(2)} €</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{ textAlign: 'right', fontSize: '12px', color: '#555' }}>
                            <div style={{ marginBottom: '3px' }}>Netto: {netto.toFixed(2)} €</div>
                            {form.mwst > 0 && <div style={{ marginBottom: '3px' }}>MwSt. {form.mwst}%: {mwstBetrag.toFixed(2)} €</div>}
                            <div style={{ fontWeight: 800, fontSize: '16px', color: '#1a1a1a', borderTop: '1px solid #e5e7eb', paddingTop: '6px', marginTop: '4px' }}>Gesamt: {gesamt.toFixed(2)} €</div>
                        </div>
                        {form.mwst === 0 && (
                            <div style={{ marginTop: '10px', padding: '6px 10px', background: '#f9fafb', borderRadius: '6px', fontSize: '10px', color: '#888', borderLeft: '3px solid #d1d5db' }}>
                                Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.
                            </div>
                        )}
                        {sender.iban && (
                            <div style={{ marginTop: '14px', borderTop: '1px solid #e5e7eb', paddingTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: '10px' }}>
                                {sender.name && <div><span style={{ color: '#aaa' }}>Kontoinhaber: </span><strong>{sender.name}</strong></div>}
                                {sender.bank && <div><span style={{ color: '#aaa' }}>Bank: </span><strong>{sender.bank}</strong></div>}
                                {sender.iban && <div><span style={{ color: '#aaa' }}>IBAN: </span><strong>{sender.iban}</strong></div>}
                                {sender.bic && <div><span style={{ color: '#aaa' }}>BIC: </span><strong>{sender.bic}</strong></div>}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {view === 'list' && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                {['Nr.', 'Projekt', 'Kunde', 'Leistungszeitraum', 'Fällig', 'Betrag', 'Status', 'PDF', ''].map(h => (
                                    <th key={h} style={{ padding: '14px 18px', textAlign: 'left', fontSize: '12px', fontWeight: 700, color: MUTED, whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map(inv => {
                                const proj = projects.find(p => p.id === inv.project_id);
                                const client = clients.find(c => c.id === inv.client_id);
                                return (
                                    <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                        <td style={{ padding: '14px 18px', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap' }}>{inv.number}</td>
                                        <td style={{ padding: '14px 18px', fontSize: '14px' }}>{proj?.title || '—'}</td>
                                        <td style={{ padding: '14px 18px', fontSize: '14px', color: MUTED }}>{client?.name || '—'}</td>
                                        <td style={{ padding: '14px 18px', fontSize: '12px', color: MUTED, whiteSpace: 'nowrap' }}>{inv.date_from && inv.date_to ? `${inv.date_from} – ${inv.date_to}` : '—'}</td>
                                        <td style={{ padding: '14px 18px', fontSize: '12px', color: MUTED, whiteSpace: 'nowrap' }}>{inv.due_date || '—'}</td>
                                        <td style={{ padding: '14px 18px', fontSize: '15px', fontWeight: 700, color: ACCENT }}>€{Number(inv.amount).toFixed(2)}</td>
                                        <td style={{ padding: '14px 18px' }}>
                                            <select value={inv.status} onChange={e => setStatus(inv.id, e.target.value)}
                                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', padding: '7px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                                <option value="offen">Offen</option><option value="gesendet">Gesendet</option>
                                                <option value="bezahlt">Bezahlt</option><option value="storniert">Storniert</option>
                                            </select>
                                        </td>
                                        <td style={{ padding: '14px 18px' }}>
                                            <button onClick={() => {
                                                const re = rechnungen.find(r => r.nummer === inv.number);
                                                let pos = [{ beschreibung: proj?.title || 'Leistung', menge: 1, preis: String(inv.amount) }];
                                                try { if (re?.positionen) pos = JSON.parse(re.positionen); } catch (_) {}
                                                const kundeName = re ? re.kunde.split('\n')[0] : (client?.name || '');
                                                const kundeAddr = re ? re.kunde.split('\n').slice(1).join('\n') : (client?.company || '');
                                                const html = buildInvoiceHTML({
                                                    nummer: inv.number,
                                                    datum: re?.datum || inv.created_at?.split('T')[0] || '',
                                                    faellig: inv.due_date,
                                                    kundenname: kundeName,
                                                    adresse: kundeAddr,
                                                    positionen: pos,
                                                    mwst: 0,
                                                    dateFrom: inv.date_from,
                                                    dateTo: inv.date_to,
                                                    sender: loadSender(),
                                                });
                                                const win = window.open('', '_blank');
                                                if (win) { win.document.write(html); win.document.close(); wireInvoiceWindow(win, inv.number); }
                                            }} className="icon-button" style={{ color: ACCENT }} title="PDF öffnen">
                                                <FileText size={15} />
                                            </button>
                                        </td>
                                        <td style={{ padding: '14px 18px' }}>
                                            <button onClick={() => del(inv.id, inv.number)} className="icon-button" style={{ color: '#ef4444' }}><Trash2 size={15} /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {invoices.length === 0 && <tr><td colSpan={9} style={{ padding: '48px', textAlign: 'center', color: MUTED }}>Noch keine Rechnungen</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ─── OFFERS ────────────────────────────────────────────────────────────────────
function OffersView({ token, offers, projects, clients, onRefresh, isMobile }) {
    const now = new Date();
    const [view, setView] = useState('list'); // 'list' | 'new'
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        client_id: '', project_id: '', datum: now.toLocaleDateString('de-DE'),
        ...EMPTY_OFFER_DATA,
    });
    const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

    const openNew = () => {
        setEditingId(null);
        setForm({ client_id: '', project_id: '', datum: now.toLocaleDateString('de-DE'), ...EMPTY_OFFER_DATA });
        setView('new');
    };

    const openEdit = (o) => {
        setEditingId(o.id);
        let data = {};
        try { data = JSON.parse(o.data || '{}'); } catch (_) { /* ignore */ }
        setForm({
            client_id: o.client_id ? String(o.client_id) : '', project_id: o.project_id ? String(o.project_id) : '',
            datum: now.toLocaleDateString('de-DE'), ...EMPTY_OFFER_DATA, ...data,
        });
        setView('new');
    };

    const closeForm = () => { setView('list'); setEditingId(null); };

    const selectClient = e => {
        const client = clients.find(c => String(c.id) === e.target.value);
        setForm(prev => ({ ...prev, client_id: e.target.value, project_id: '', kundenname: client?.name || prev.kundenname, adresse: client?.company || prev.adresse }));
    };

    const selectProject = e => {
        const proj = projects.find(p => String(p.id) === e.target.value);
        setForm(prev => ({
            ...prev, project_id: e.target.value,
            titel: proj?.title || prev.titel,
            positionen: proj ? [{ beschreibung: proj.title, menge: 1, preis: String(proj.price || '') }] : prev.positionen,
        }));
    };

    const nextNumber = useMemo(() => {
        const initials = getInitials(form.kundenname);
        const prefix = `AN-${initials}-`;
        const parseNum = s => parseInt((s || '').replace(prefix, '')) || 0;
        const maxNum = Math.max(0, ...offers.map(o => o.number || '').filter(n => n.startsWith(prefix)).map(parseNum));
        return `${prefix}${String(maxNum + 1).padStart(3, '0')}`;
    }, [offers, form.kundenname]);

    const updatePos = (i, field, val) => setForm(p => ({ ...p, positionen: p.positionen.map((pos, idx) => idx === i ? { ...pos, [field]: val } : pos) }));
    const addPos = () => setForm(p => ({ ...p, positionen: [...p.positionen, emptyOfferPos()] }));
    const removePos = i => setForm(p => ({ ...p, positionen: p.positionen.filter((_, idx) => idx !== i) }));

    const netto = form.positionen.reduce((s, p) => s + (parseFloat(p.preis) || 0) * (parseFloat(p.menge) || 0), 0);
    const mwstBetrag = netto * (form.mwst / 100);
    const gesamt = netto + mwstBetrag;

    const handleSave = async (openPrint) => {
        if (!form.kundenname) return;
        setSaving(true);
        const { client_id, project_id, datum, ...data } = form;
        const number = editingId ? (offers.find(o => o.id === editingId)?.number || nextNumber) : nextNumber;
        const body = { client_id: parseInt(client_id) || 0, project_id: parseInt(project_id) || 0, number, data: { ...data, datum } };
        const res = await fetch(`${API}/api/essentials/offers${editingId ? `/${editingId}` : ''}`, {
            method: editingId ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body),
        });
        setSaving(false);
        if (openPrint) {
            const html = buildOfferHTML({ nummer: number, datum: form.datum, gueltigBis: form.gueltigBis, kundenname: form.kundenname, adresse: form.adresse, titel: form.titel, anforderungen: form.anforderungen, design: form.design, positionen: form.positionen, mwst: form.mwst });
            const win = window.open('', '_blank');
            if (win) { win.document.write(html); win.document.close(); wireOfferWindow(win, `Angebot-${number}`); }
        }
        closeForm();
        onRefresh();
        return res.ok ? res.json() : null;
    };

    const setStatus = async (id, status) => {
        await fetch(`${API}/api/essentials/offers/${id}/status`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ status }),
        });
        onRefresh();
    };

    const del = async (id, number) => {
        if (!window.confirm(`Angebot ${number || ''} wirklich löschen? Das kann nicht rückgängig gemacht werden.`)) return;
        await fetch(`${API}/api/essentials/offers/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        onRefresh();
    };

    const printExisting = (o) => {
        let data = {};
        try { data = JSON.parse(o.data || '{}'); } catch (_) { /* ignore */ }
        const html = buildOfferHTML({ nummer: o.number, datum: data.datum, gueltigBis: data.gueltigBis, kundenname: data.kundenname, adresse: data.adresse, titel: data.titel, anforderungen: data.anforderungen, design: data.design, positionen: data.positionen, mwst: data.mwst });
        const win = window.open('', '_blank');
        if (win) { win.document.write(html); win.document.close(); wireOfferWindow(win, `Angebot-${o.number}`); }
    };

    const offerAmount = o => {
        let data = {};
        try { data = JSON.parse(o.data || '{}'); } catch (_) { return 0; }
        const n = (data.positionen || []).reduce((s, p) => s + (parseFloat(p.preis) || 0) * (parseFloat(p.menge) || 0), 0);
        return n * (1 + (data.mwst || 0) / 100);
    };

    const clientName = id => clients.find(c => c.id === id)?.name || '—';
    const projectTitle = id => projects.find(p => p.id === id)?.title || '';
    const clientProjects = projects.filter(p => String(p.client_id) === form.client_id);
    const repriceInput = { ...inputStyle, padding: '9px 12px', fontSize: '14px' };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
                <div>
                    <h1 className="section-title" style={{ marginBottom: '4px' }}>Angebote</h1>
                    <p className="section-subtitle">{view === 'new' ? `${editingId ? 'Angebot bearbeiten' : 'Neues Angebot'} · ${nextNumber}` : 'Anforderungen, Funktionsumfang und Kalkulation vor Vertragsschluss festhalten'}</p>
                </div>
                <button onClick={() => (view === 'list' ? openNew() : closeForm())} className="btn-primary">
                    {view === 'new' ? '← Zur Liste' : <><Plus size={16} /> Neues Angebot</>}
                </button>
            </div>

            {view === 'new' && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px', display: 'flex', flexDirection: 'column', gap: '18px', maxWidth: '760px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
                        <div>
                            <label className="form-label">Kunde *</label>
                            <select value={form.client_id} onChange={selectClient} style={selectStyle}>
                                <option value="">— Kunde wählen —</option>
                                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="form-label">Projekt (Optional)</label>
                            <select value={form.project_id} onChange={selectProject} style={selectStyle} disabled={!form.client_id}>
                                <option value="">— Kein bestimmtes Projekt —</option>
                                {clientProjects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
                        <div><label className="form-label">Kundenname *</label><input value={form.kundenname} onChange={f('kundenname')} style={inputStyle} placeholder="Max Mustermann" /></div>
                        <div><label className="form-label">Firma / Adresse</label><input value={form.adresse} onChange={f('adresse')} style={inputStyle} placeholder="Firma GmbH, Musterstr. 1" /></div>
                    </div>

                    <div><label className="form-label">Titel</label><input value={form.titel} onChange={f('titel')} style={inputStyle} placeholder="z.B. Website-Neubau MiS" /></div>

                    <div>
                        <label className="form-label">Anforderungen &amp; Funktionsumfang</label>
                        <textarea value={form.anforderungen} onChange={f('anforderungen')} rows={4} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} placeholder={'z.B.\n- 5 Unterseiten (Home, Über uns, Leistungen, Kontakt, Impressum)\n- Kontaktformular mit E-Mail-Versand\n- Responsive für Mobile/Tablet'} />
                    </div>
                    <div>
                        <label className="form-label">Design</label>
                        <textarea value={form.design} onChange={f('design')} rows={3} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} placeholder="z.B. Farbpalette, Stil, Referenzen" />
                    </div>

                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <label className="form-label" style={{ margin: 0 }}>Kalkulation</label>
                            <button type="button" onClick={addPos} className="icon-button" style={{ fontSize: '13px', padding: '5px 12px', border: '1px solid var(--border)', borderRadius: '6px' }}><Plus size={13} /> Position</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 28px', gap: '6px', fontSize: '11px', color: MUTED, padding: '0 4px', marginBottom: '4px' }}>
                            <span>Beschreibung</span><span style={{ textAlign: 'center' }}>Menge</span><span style={{ textAlign: 'right' }}>Preis €</span><span />
                        </div>
                        {form.positionen.map((p, i) => (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 28px', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                                <input value={p.beschreibung} onChange={e => updatePos(i, 'beschreibung', e.target.value)} style={repriceInput} placeholder="Leistungsbeschreibung" />
                                <input value={p.menge} onChange={e => updatePos(i, 'menge', e.target.value)} style={{ ...repriceInput, textAlign: 'center' }} />
                                <input value={p.preis} onChange={e => updatePos(i, 'preis', e.target.value.replace(',', '.'))} style={{ ...repriceInput, textAlign: 'right' }} placeholder="0.00" />
                                {form.positionen.length > 1
                                    ? <button type="button" onClick={() => removePos(i)} className="icon-button" style={{ color: '#ef4444', padding: 0 }}><Trash2 size={14} /></button>
                                    : <div />}
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
                        <div>
                            <label className="form-label">MwSt.</label>
                            <select value={form.mwst} onChange={e => setForm(p => ({ ...p, mwst: Number(e.target.value) }))} style={selectStyle}>
                                <option value={0}>0 % (Kleinunternehmer)</option>
                                <option value={7}>7 %</option>
                                <option value={19}>19 %</option>
                            </select>
                        </div>
                        <div><label className="form-label">Gültig bis</label><input value={form.gueltigBis} onChange={f('gueltigBis')} style={inputStyle} placeholder="TT.MM.JJJJ" maxLength={10} /></div>
                    </div>

                    <div style={{ background: 'var(--bg-secondary)', borderRadius: '10px', padding: '14px 18px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: MUTED, marginBottom: '6px' }}><span>Netto</span><span>{netto.toFixed(2)} €</span></div>
                        {form.mwst > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: MUTED, marginBottom: '6px' }}><span>MwSt. {form.mwst}%</span><span>{mwstBetrag.toFixed(2)} €</span></div>}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '18px', borderTop: '1px solid var(--border)', paddingTop: '8px', color: ACCENT }}><span>Gesamt</span><span>{gesamt.toFixed(2)} €</span></div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <button type="button" onClick={() => handleSave(true)} className="btn-primary" disabled={saving || !form.kundenname} style={{ fontSize: '15px', padding: '14px' }}>
                            {saving ? 'Wird gespeichert…' : '💾 Speichern & drucken'}
                        </button>
                        <button type="button" onClick={() => handleSave(false)} className="icon-button" disabled={saving || !form.kundenname}
                            style={{ border: '2px solid var(--border)', borderRadius: '10px', padding: '11px 22px', fontSize: '15px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                            Nur speichern
                        </button>
                        <CancelBtn onClick={closeForm} />
                    </div>
                </div>
            )}

            {view === 'list' && offers.length === 0 && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '48px', textAlign: 'center', color: MUTED }}>Noch keine Angebote</div>
            )}

            {view === 'list' && offers.length > 0 && isMobile && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {offers.map(o => (
                        <div key={o.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                                <div>
                                    <div style={{ fontSize: '13px', color: MUTED, fontWeight: 700 }}>{o.number}</div>
                                    <div style={{ fontSize: '15px', fontWeight: 700 }}>{clientName(o.client_id)}</div>
                                    {projectTitle(o.project_id) && <div style={{ fontSize: '13px', color: MUTED }}>{projectTitle(o.project_id)}</div>}
                                </div>
                                <Badge status={o.status} />
                            </div>
                            <div style={{ fontSize: '18px', fontWeight: 800, color: ACCENT, margin: '8px 0' }}>€{offerAmount(o).toFixed(2)}</div>
                            <select value={o.status} onChange={e => setStatus(o.id, e.target.value)}
                                style={{ ...selectStyle, marginBottom: '12px', fontSize: '13px', padding: '9px 12px' }}>
                                <option value="offen">Offen</option><option value="angenommen">Angenommen</option><option value="abgelehnt">Abgelehnt</option>
                            </select>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => printExisting(o)} className="icon-button" style={{ flex: 1, justifyContent: 'center', gap: '6px', color: ACCENT, border: '1px solid var(--border)', borderRadius: '8px', padding: '9px' }}><FileText size={15} /> Drucken</button>
                                <button onClick={() => openEdit(o)} className="icon-button" style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '9px 12px', color: MUTED }}><Pencil size={15} /></button>
                                <button onClick={() => del(o.id, o.number)} className="icon-button" style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '9px 12px', color: '#ef4444' }}><Trash2 size={15} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {view === 'list' && offers.length > 0 && !isMobile && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                {['Nr.', 'Kunde', 'Projekt', 'Betrag', 'Gültig bis', 'Status', ''].map(h => (
                                    <th key={h} style={{ padding: '14px 18px', textAlign: 'left', fontSize: '12px', fontWeight: 700, color: MUTED, whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {offers.map(o => {
                                let data = {};
                                try { data = JSON.parse(o.data || '{}'); } catch (_) { /* ignore */ }
                                return (
                                    <tr key={o.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                        <td style={{ padding: '14px 18px', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap' }}>{o.number}</td>
                                        <td style={{ padding: '14px 18px', fontSize: '14px' }}>{clientName(o.client_id)}</td>
                                        <td style={{ padding: '14px 18px', fontSize: '14px', color: MUTED }}>{projectTitle(o.project_id) || '—'}</td>
                                        <td style={{ padding: '14px 18px', fontSize: '15px', fontWeight: 700, color: ACCENT }}>€{offerAmount(o).toFixed(2)}</td>
                                        <td style={{ padding: '14px 18px', fontSize: '12px', color: MUTED, whiteSpace: 'nowrap' }}>{data.gueltigBis || '—'}</td>
                                        <td style={{ padding: '14px 18px' }}>
                                            <select value={o.status} onChange={e => setStatus(o.id, e.target.value)}
                                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', padding: '7px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                                <option value="offen">Offen</option><option value="angenommen">Angenommen</option><option value="abgelehnt">Abgelehnt</option>
                                            </select>
                                        </td>
                                        <td style={{ padding: '14px 18px', display: 'flex', gap: '4px' }}>
                                            <button onClick={() => printExisting(o)} className="icon-button" style={{ color: ACCENT }} title="Drucken/PDF"><FileText size={15} /></button>
                                            <button onClick={() => openEdit(o)} className="icon-button" style={{ color: MUTED }} title="Bearbeiten"><Pencil size={15} /></button>
                                            <button onClick={() => del(o.id, o.number)} className="icon-button" style={{ color: '#ef4444' }} title="Löschen"><Trash2 size={15} /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ─── CONTRACTS ─────────────────────────────────────────────────────────────────
function ContractsView({ token, contracts, projects, clients, onRefresh, isMobile }) {
    const [view, setView] = useState('list'); // 'list' | 'new'
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploadingId, setUploadingId] = useState(null);
    const [form, setForm] = useState({ client_id: '', project_id: '', ...EMPTY_CONTRACT_DATA });
    const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

    const openNew = () => {
        setEditingId(null);
        const sender = loadSender();
        const defaults = loadContractDefaults();
        setForm({
            client_id: '', project_id: '', ...EMPTY_CONTRACT_DATA,
            auftragnehmerAnschrift: sender.street ? `${sender.street}\n${sender.city}` : '',
            agbUrl: defaults.agbUrl, kuendigungsfrist: defaults.kuendigungsfrist,
        });
        setView('new');
    };

    const openEdit = (c) => {
        setEditingId(c.id);
        let data = {};
        try { data = JSON.parse(c.data || '{}'); } catch (_) { /* ignore */ }
        setForm({
            client_id: c.client_id ? String(c.client_id) : '',
            project_id: c.project_id ? String(c.project_id) : '',
            ...EMPTY_CONTRACT_DATA, ...data,
        });
        setView('new');
    };

    const closeForm = () => { setView('list'); setEditingId(null); };

    const toggleLeistung = (l) => setForm(p => ({
        ...p,
        leistungen: p.leistungen.includes(l) ? p.leistungen.filter(x => x !== l) : [...p.leistungen, l],
    }));

    const selectClient = e => {
        const client = clients.find(c => String(c.id) === e.target.value);
        setForm(prev => ({
            ...prev, client_id: e.target.value, project_id: '',
            auftraggeberName: client?.name || prev.auftraggeberName,
            auftraggeberAnschrift: client?.company || prev.auftraggeberAnschrift,
        }));
    };

    const handleSave = async (openPrint) => {
        if (!form.client_id) return;
        setSaving(true);
        saveContractDefaults({ agbUrl: form.agbUrl, kuendigungsfrist: form.kuendigungsfrist });
        const { client_id, project_id, ...data } = form;
        const res = await fetch(`${API}/api/essentials/contracts`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ client_id: parseInt(client_id), project_id: parseInt(project_id) || 0, data }),
        });
        setSaving(false);
        if (openPrint) {
            const client = clients.find(c => String(c.id) === client_id);
            const html = buildContractHTML(data);
            const win = window.open('', '_blank');
            if (win) { win.document.write(html); win.document.close(); wireContractWindow(win, `Vertrag-${client?.name || 'Kunde'}`); }
        }
        closeForm();
        onRefresh();
        return res.ok ? res.json() : null;
    };

    const del = async (id, name) => {
        if (!window.confirm(`Vertrag für "${name}" wirklich löschen? Das kann nicht rückgängig gemacht werden.`)) return;
        await fetch(`${API}/api/essentials/contracts/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        onRefresh();
    };

    const printExisting = (c) => {
        let data = {};
        try { data = JSON.parse(c.data || '{}'); } catch (_) { /* ignore */ }
        const client = clients.find(cl => cl.id === c.client_id);
        const html = buildContractHTML(data);
        const win = window.open('', '_blank');
        if (win) { win.document.write(html); win.document.close(); wireContractWindow(win, `Vertrag-${client?.name || 'Kunde'}`); }
    };

    const uploadSigned = async (id, file) => {
        if (!file) return;
        setUploadingId(id);
        const body = new FormData();
        body.append('file', file);
        await fetch(`${API}/api/essentials/contracts/${id}/signed`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body,
        });
        setUploadingId(null);
        onRefresh();
    };

    const removeSigned = async (id, name) => {
        if (!window.confirm(`Unterschriebene Version für "${name}" wirklich entfernen?`)) return;
        await fetch(`${API}/api/essentials/contracts/${id}/signed`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        onRefresh();
    };

    const downloadSigned = (id) => {
        fetch(`${API}/api/essentials/contracts/${id}/signed`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.ok ? r.blob() : Promise.reject())
            .then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'Vertrag-unterschrieben';
                a.click();
                URL.revokeObjectURL(url);
            })
            .catch(() => window.alert('Datei konnte nicht geladen werden.'));
    };

    const clientName = id => clients.find(c => c.id === id)?.name || '—';
    const projectTitle = id => projects.find(p => p.id === id)?.title || '';
    const clientProjects = projects.filter(p => String(p.client_id) === form.client_id);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
                <div>
                    <h1 className="section-title" style={{ marginBottom: '4px' }}>Verträge</h1>
                    <p className="section-subtitle">{view === 'new' ? (editingId ? 'Vertrag bearbeiten' : 'Neuer Dienstleistungsvertrag') : 'Dienstleistungsverträge ausfüllen, drucken und beim Kunden ablegen'}</p>
                </div>
                <button onClick={() => (view === 'list' ? openNew() : closeForm())} className="btn-primary">
                    {view === 'new' ? '← Zur Liste' : <><Plus size={16} /> Neuer Vertrag</>}
                </button>
            </div>

            {view === 'new' && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px', display: 'flex', flexDirection: 'column', gap: '18px', maxWidth: '760px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
                        <div>
                            <label className="form-label">Kunde *</label>
                            <select value={form.client_id} onChange={selectClient} style={selectStyle}>
                                <option value="">— Kunde wählen —</option>
                                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="form-label">Projekt (Optional)</label>
                            <select value={form.project_id} onChange={f('project_id')} style={selectStyle} disabled={!form.client_id}>
                                <option value="">— Kein bestimmtes Projekt —</option>
                                {clientProjects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
                        <div><label className="form-label">Auftraggeber Name / Firma</label><input value={form.auftraggeberName} onChange={f('auftraggeberName')} style={inputStyle} placeholder="Max Mustermann / Firma GmbH" /></div>
                        <div><label className="form-label">Auftraggeber Anschrift</label><input value={form.auftraggeberAnschrift} onChange={f('auftraggeberAnschrift')} style={inputStyle} placeholder="Musterstr. 1, 12345 Musterstadt" /></div>
                    </div>
                    <div><label className="form-label">Auftragnehmer Anschrift (MiS)</label><input value={form.auftragnehmerAnschrift} onChange={f('auftragnehmerAnschrift')} style={inputStyle} placeholder="Straße, PLZ Stadt" /></div>

                    <div>
                        <label className="form-label">Leistung (§ 1)</label>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '6px', marginTop: '4px' }}>
                            {LEISTUNGEN.map(l => (
                                <label key={l} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={form.leistungen.includes(l)} onChange={() => toggleLeistung(l)} />
                                    {l}
                                </label>
                            ))}
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                                <input type="checkbox" checked={form.leistungen.includes('Sonstiges')} onChange={() => toggleLeistung('Sonstiges')} />
                                Sonstiges:
                            </label>
                        </div>
                        {form.leistungen.includes('Sonstiges') && (
                            <input value={form.sonstigesText} onChange={f('sonstigesText')} style={{ ...inputStyle, marginTop: '8px' }} placeholder="Beschreibung" />
                        )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '14px' }}>
                        <div><label className="form-label">Vergütung (€)</label><input value={form.verguetungBetrag} onChange={f('verguetungBetrag')} style={inputStyle} placeholder="0" /></div>
                        <div>
                            <label className="form-label">Netto/Brutto</label>
                            <select value={form.verguetungTyp} onChange={f('verguetungTyp')} style={selectStyle}>
                                <option value="netto">Netto</option><option value="brutto">Brutto</option>
                            </select>
                        </div>
                        <div>
                            <label className="form-label">Zahlungsart</label>
                            <select value={form.zahlungsart} onChange={f('zahlungsart')} style={selectStyle}>
                                <option value="pauschal">Preis</option><option value="stundensatz">Stundensatz</option>
                            </select>
                        </div>
                    </div>
                    {form.zahlungsart === 'stundensatz' && (
                        <div><label className="form-label">Stundensatz (€ / Stunde)</label><input value={form.stundensatz} onChange={f('stundensatz')} style={inputStyle} placeholder="0" /></div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
                        <div><label className="form-label">Projektbeginn</label><input value={form.projektbeginn} onChange={f('projektbeginn')} style={inputStyle} placeholder="TT.MM.JJJJ" /></div>
                        <div><label className="form-label">Fertigstellung</label><input value={form.projektfertigstellung} onChange={f('projektfertigstellung')} style={inputStyle} placeholder="TT.MM.JJJJ" /></div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
                        <div><label className="form-label">Kündigungsfrist (Monate)</label><input value={form.kuendigungsfrist} onChange={f('kuendigungsfrist')} style={inputStyle} placeholder="1" /></div>
                        <div><label className="form-label">AGB/Datenschutz-Link</label><input value={form.agbUrl} onChange={f('agbUrl')} style={inputStyle} placeholder="harderlino.de/agb" /></div>
                    </div>
                    <div>
                        <label className="form-label">Individuelle Vereinbarungen <span style={{ color: MUTED, fontWeight: 400 }}>(Optional, erscheint als § 9)</span></label>
                        <textarea value={form.notizen} onChange={f('notizen')} rows={8} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} placeholder={'z.B. Was nicht enthalten ist, oder Zusatzvereinbarungen wie "Ab sofort 30 € / Monat für laufende Wartung"'} />
                    </div>
                    <div><label className="form-label">Ort, Datum</label><input value={form.ortDatum} onChange={f('ortDatum')} style={inputStyle} placeholder="Rostock, TT.MM.JJJJ" /></div>

                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <button type="button" onClick={() => handleSave(true)} className="btn-primary" disabled={saving || !form.client_id} style={{ fontSize: '15px', padding: '14px' }}>
                            {saving ? 'Wird gespeichert…' : '💾 Speichern & drucken'}
                        </button>
                        <button type="button" onClick={() => handleSave(false)} className="icon-button" disabled={saving || !form.client_id}
                            style={{ border: '2px solid var(--border)', borderRadius: '10px', padding: '11px 22px', fontSize: '15px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                            Nur speichern
                        </button>
                        <CancelBtn onClick={closeForm} />
                    </div>
                </div>
            )}

            {view === 'list' && contracts.length === 0 && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '48px', textAlign: 'center', color: MUTED }}>Noch keine Verträge</div>
            )}

            {view === 'list' && contracts.length > 0 && isMobile && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {contracts.map(c => (
                        <div key={c.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                                <div>
                                    <div style={{ fontSize: '15px', fontWeight: 700 }}>{clientName(c.client_id)}</div>
                                    {projectTitle(c.project_id) && <div style={{ fontSize: '13px', color: MUTED }}>{projectTitle(c.project_id)}</div>}
                                </div>
                                <Badge status={c.status} />
                            </div>
                            <div style={{ fontSize: '11px', color: MUTED, marginBottom: '12px' }}>Geändert: {(c.updated_at || c.created_at || '').split('T')[0] || '—'}</div>

                            {c.signed_filename ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-secondary)', borderRadius: '8px', padding: '8px 10px', marginBottom: '12px' }}>
                                    <span style={{ fontSize: '12px', color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.signed_original_name}</span>
                                    <button onClick={() => downloadSigned(c.id)} className="icon-button" style={{ color: '#4ade80' }} title="Herunterladen"><Download size={16} /></button>
                                    <button onClick={() => removeSigned(c.id, clientName(c.client_id))} className="icon-button" style={{ color: '#ef4444' }} title="Entfernen"><X size={16} /></button>
                                </div>
                            ) : (
                                <label className="icon-button" style={{ display: 'flex', justifyContent: 'center', gap: '6px', color: ACCENT, cursor: 'pointer', fontSize: '13px', fontWeight: 600, border: '1px dashed var(--border)', borderRadius: '8px', padding: '10px', marginBottom: '12px' }}>
                                    <Upload size={15} /> {uploadingId === c.id ? 'Lädt hoch…' : 'Unterschriebene Version hochladen'}
                                    <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
                                        onChange={e => uploadSigned(c.id, e.target.files?.[0])} />
                                </label>
                            )}

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => printExisting(c)} className="icon-button" style={{ flex: 1, justifyContent: 'center', gap: '6px', color: ACCENT, border: '1px solid var(--border)', borderRadius: '8px', padding: '9px' }}><FileSignature size={15} /> Drucken</button>
                                <button onClick={() => openEdit(c)} className="icon-button" style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '9px 12px', color: MUTED }}><Pencil size={15} /></button>
                                <button onClick={() => del(c.id, clientName(c.client_id))} className="icon-button" style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '9px 12px', color: '#ef4444' }}><Trash2 size={15} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {view === 'list' && contracts.length > 0 && !isMobile && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                {['Kunde', 'Projekt', 'Status', 'Zuletzt geändert', 'Unterschriebene Version', ''].map(h => (
                                    <th key={h} style={{ padding: '14px 18px', textAlign: 'left', fontSize: '12px', fontWeight: 700, color: MUTED, whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {contracts.map(c => (
                                <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                    <td style={{ padding: '14px 18px', fontSize: '14px', fontWeight: 600 }}>{clientName(c.client_id)}</td>
                                    <td style={{ padding: '14px 18px', fontSize: '14px', color: MUTED }}>{projectTitle(c.project_id) || '—'}</td>
                                    <td style={{ padding: '14px 18px' }}><Badge status={c.status} /></td>
                                    <td style={{ padding: '14px 18px', fontSize: '12px', color: MUTED, whiteSpace: 'nowrap' }}>{(c.updated_at || c.created_at || '').split('T')[0] || '—'}</td>
                                    <td style={{ padding: '14px 18px' }}>
                                        {c.signed_filename ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <button onClick={() => downloadSigned(c.id)} className="icon-button" style={{ color: '#4ade80' }} title="Herunterladen"><Download size={15} /></button>
                                                <span style={{ fontSize: '12px', color: MUTED, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.signed_original_name}</span>
                                                <button onClick={() => removeSigned(c.id, clientName(c.client_id))} className="icon-button" style={{ color: '#ef4444' }} title="Entfernen"><X size={15} /></button>
                                            </div>
                                        ) : (
                                            <label className="icon-button" style={{ color: ACCENT, cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                                                <Upload size={15} /> {uploadingId === c.id ? 'Lädt hoch…' : 'Hochladen'}
                                                <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
                                                    onChange={e => uploadSigned(c.id, e.target.files?.[0])} />
                                            </label>
                                        )}
                                    </td>
                                    <td style={{ padding: '14px 18px', display: 'flex', gap: '4px' }}>
                                        <button onClick={() => printExisting(c)} className="icon-button" style={{ color: ACCENT }} title="Drucken/PDF"><FileSignature size={15} /></button>
                                        <button onClick={() => openEdit(c)} className="icon-button" style={{ color: MUTED }} title="Bearbeiten"><Pencil size={15} /></button>
                                        <button onClick={() => del(c.id, clientName(c.client_id))} className="icon-button" style={{ color: '#ef4444' }} title="Löschen"><Trash2 size={15} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ─── CALENDAR ──────────────────────────────────────────────────────────────────
function CalendarView({ token, events, onRefresh, isMobile }) {
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth());
    const [selectedDay, setSelectedDay] = useState(today.getDate());
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ title: '', date: '', description: '' });

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const dateKey = d => `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const eventsOnDay = d => events.filter(ev => ev.date === dateKey(d));
    const selectedEvents = eventsOnDay(selectedDay);

    const prevMonth = () => month === 0 ? (setMonth(11), setYear(y => y - 1)) : setMonth(m => m - 1);
    const nextMonth = () => month === 11 ? (setMonth(0), setYear(y => y + 1)) : setMonth(m => m + 1);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!form.title || !form.date) return;
        await fetch(`${API}/api/essentials/events`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(form)
        });
        setForm({ title: '', date: dateKey(selectedDay), description: '' });
        setShowForm(false);
        onRefresh();
    };

    const del = async (id, title) => {
        if (!window.confirm(`Termin "${title || ''}" wirklich löschen?`)) return;
        await fetch(`${API}/api/essentials/events/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        onRefresh();
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
                <div>
                    <h1 className="section-title" style={{ marginBottom: '4px' }}>Kalender</h1>
                    <p className="section-subtitle">Deine Termine und Projekt-Meilensteine</p>
                </div>
                <button onClick={() => { setForm({ title: '', date: dateKey(selectedDay), description: '' }); setShowForm(s => !s); }} className="btn-primary">
                    <Plus size={16} /> Neuer Termin
                </button>
            </div>

            {showForm && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px', marginBottom: '28px' }}>
                    <form onSubmit={handleAdd} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                        <div><label className="form-label">Titel *</label><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inputStyle} placeholder="Terminname" /></div>
                        <div><label className="form-label">Datum *</label><input type="text" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={inputStyle} placeholder="TT.MM.JJJJ" maxLength={10} /></div>
                        <div style={{ gridColumn: '1 / -1' }}><label className="form-label">Beschreibung <span style={{ color: MUTED, fontWeight: 400 }}>(Optional)</span></label><input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={inputStyle} placeholder="Optional" /></div>
                        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px' }}>
                            <button type="submit" className="btn-primary">Speichern</button>
                            <CancelBtn onClick={() => setShowForm(false)} />
                        </div>
                    </form>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 280px', gap: '24px' }}>
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <button onClick={prevMonth} className="icon-button"><ChevronLeft size={20} /></button>
                        <span style={{ fontWeight: 700, fontSize: '16px' }}>{MONTHS_DE[month]} {year}</span>
                        <button onClick={nextMonth} className="icon-button"><ChevronRight size={20} /></button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                        {DAYS_DE.map(d => <div key={d} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 700, color: MUTED, padding: '6px 0' }}>{d}</div>)}
                        {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                            const day = i + 1;
                            const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
                            const isSelected = day === selectedDay;
                            const hasEv = eventsOnDay(day).length > 0;
                            return (
                                <button key={day} onClick={() => setSelectedDay(day)} style={{
                                    textAlign: 'center', fontSize: '14px', padding: '8px 4px', border: 'none', borderRadius: '8px',
                                    cursor: 'pointer', position: 'relative', fontFamily: 'inherit',
                                    background: isSelected ? ACCENT : isToday ? `${ACCENT}22` : 'transparent',
                                    color: isSelected ? '#000' : isToday ? ACCENT : 'var(--text-primary)',
                                    fontWeight: isSelected || isToday ? 700 : 400,
                                }}>
                                    {day}
                                    {hasEv && <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: isSelected ? '#000' : ACCENT, position: 'absolute', bottom: '2px', left: '50%', transform: 'translateX(-50%)' }} />}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px' }}>
                    <h3 className="project-title" style={{ marginBottom: '4px' }}>{selectedDay}. {MONTHS_DE[month]} {year}</h3>
                    <p className="project-description" style={{ marginBottom: '16px' }}>{selectedEvents.length === 0 ? 'Keine Termine' : `${selectedEvents.length} Termin${selectedEvents.length > 1 ? 'e' : ''}`}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {selectedEvents.map(ev => (
                            <div key={ev.id} style={{ background: `${ACCENT}12`, border: `1px solid ${ACCENT}33`, borderRadius: '10px', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <div style={{ fontWeight: 600, marginBottom: '2px' }}>{ev.title}</div>
                                    {ev.description && <div className="project-description">{ev.description}</div>}
                                </div>
                                <button onClick={() => del(ev.id, ev.title)} className="icon-button" style={{ color: '#ef4444', padding: '0 0 0 8px' }}><Trash2 size={14} /></button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── FINANCIALS ────────────────────────────────────────────────────────────────
function FinancialsView({ invoices, projects, isMobile }) {
    const now = new Date();
    const paid = useMemo(() => invoices.filter(i => i.status === 'bezahlt'), [invoices]);
    const pending = useMemo(() => invoices.filter(i => i.status !== 'bezahlt' && i.status !== 'storniert'), [invoices]);
    const overdue = useMemo(() => pending.filter(i => i.due_date && new Date(i.due_date) < now), [pending]);

    const thisMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const totalRevenue = paid.reduce((s, i) => s + i.amount, 0);
    const thisMonthRevenue = paid.filter(i => (i.created_at || '').startsWith(thisMonthKey)).reduce((s, i) => s + i.amount, 0);
    const thisYearRevenue = paid.filter(i => (i.created_at || '').startsWith(String(now.getFullYear()))).reduce((s, i) => s + i.amount, 0);
    const totalPending = pending.reduce((s, i) => s + i.amount, 0);
    const totalOverdue = overdue.reduce((s, i) => s + i.amount, 0);

    const monthlyData = useMemo(() => Array.from({ length: 12 }, (_, idx) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (11 - idx), 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return { label: d.toLocaleDateString('de-DE', { month: 'short' }), amount: paid.filter(inv => (inv.created_at || '').startsWith(key)).reduce((s, inv) => s + inv.amount, 0) };
    }), [paid]);

    const topProjects = useMemo(() => {
        const byProject = {};
        paid.forEach(inv => { byProject[inv.project_id] = (byProject[inv.project_id] || 0) + inv.amount; });
        return Object.entries(byProject).map(([id, amount]) => ({ id: parseInt(id), amount, title: projects.find(p => p.id === parseInt(id))?.title || '—' })).sort((a, b) => b.amount - a.amount).slice(0, 5);
    }, [paid, projects]);

    return (
        <div>
            <h1 className="section-title" style={{ marginBottom: '8px' }}>Finanzen</h1>
            <p className="section-subtitle" style={{ marginBottom: '32px' }}>Überblick über deine Einnahmen</p>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '16px', marginBottom: '28px' }}>
                <StatCard label="Gesamtumsatz" value={`€${totalRevenue.toFixed(0)}`} sub="Alle bezahlten Rechnungen" />
                <StatCard label="Dieser Monat" value={`€${thisMonthRevenue.toFixed(0)}`} sub={MONTHS_DE[now.getMonth()]} color="#60a5fa" />
                <StatCard label="Dieses Jahr" value={`€${thisYearRevenue.toFixed(0)}`} sub={String(now.getFullYear())} color="#a78bfa" />
                <StatCard label="Ausstehend" value={`€${totalPending.toFixed(0)}`} sub={`${pending.length} offene Rechnungen`} color="#f59e0b" />
            </div>

            {totalOverdue > 0 && (
                <div style={{ background: '#ef444418', border: '1px solid #ef4444', borderRadius: '10px', padding: '14px 20px', marginBottom: '28px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <span style={{ color: '#ef4444', fontWeight: 700 }}>⚠ Überfällige Zahlungen: €{totalOverdue.toFixed(0)}</span>
                    <span style={{ color: MUTED, fontSize: '13px' }}>({overdue.length} Rechnung{overdue.length > 1 ? 'en' : ''})</span>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 300px', gap: '24px' }}>
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px' }}>
                    <h2 className="project-title" style={{ marginBottom: '20px' }}>Monatlicher Umsatz (Letzte 12 Monate)</h2>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                            <XAxis dataKey="label" tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px' }} formatter={v => [`€${v}`, 'Umsatz']} />
                            <Bar dataKey="amount" fill={ACCENT} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px' }}>
                    <h2 className="project-title" style={{ marginBottom: '20px' }}>Top Projekte nach Umsatz</h2>
                    {topProjects.length === 0 && <p className="project-description">Noch keine Daten</p>}
                    {topProjects.map((p, i) => (
                        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '12px', color: MUTED, fontWeight: 700 }}>{i + 1}</span>
                                <span style={{ fontSize: '14px', fontWeight: 600 }}>{p.title}</span>
                            </div>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: ACCENT }}>€{p.amount}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ─── OUTREACH ──────────────────────────────────────────────────────────────────
function OutreachView({ token, leads, onRefresh, isMobile }) {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ company: '', contact: '', email: '', product_niche: '', status: 'Neu', notes: '', priority: 'Mittel' });
    const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

    const handleAdd = async (e) => {
        e.preventDefault();
        await fetch(`${API}/api/essentials/leads`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(form)
        });
        setForm({ company: '', contact: '', email: '', product_niche: '', status: 'Neu', notes: '', priority: 'Mittel' });
        setShowForm(false);
        onRefresh();
    };

    const updateStatus = async (id, status) => {
        const lead = leads.find(l => l.id === id);
        await fetch(`${API}/api/essentials/leads/${id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ status, email_sent: lead?.email_sent || false, date_sent: lead?.date_sent || '', followup: lead?.followup || false, notes: lead?.notes || '' })
        });
        onRefresh();
    };

    const del = async (id, company) => {
        if (!window.confirm(`Lead "${company || ''}" wirklich löschen?`)) return;
        await fetch(`${API}/api/essentials/leads/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        onRefresh();
    };

    const stats = {
        total: leads.length,
        contacted: leads.filter(l => l.status !== 'Neu').length,
        replies: leads.filter(l => ['Angebot', 'Gewonnen'].includes(l.status)).length,
        won: leads.filter(l => l.status === 'Gewonnen').length,
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h1 className="section-title" style={{ marginBottom: '4px' }}>Outreach</h1>
                    <p className="section-subtitle">Cold-E-Mail CRM für Leads</p>
                </div>
                <button onClick={() => setShowForm(s => !s)} className="btn-primary"><Plus size={16} /> Lead hinzufügen</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '16px', marginBottom: '28px' }}>
                <StatCard label="Total Leads" value={stats.total} color="#60a5fa" />
                <StatCard label="Kontaktiert" value={stats.contacted} color={ACCENT} />
                <StatCard label="Replies" value={stats.replies} color="#a78bfa" />
                <StatCard label="Gewonnen" value={stats.won} color="#4ade80" />
            </div>

            {showForm && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px', marginBottom: '28px' }}>
                    <form onSubmit={handleAdd} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                        <div><label className="form-label">Firma</label><input value={form.company} onChange={f('company')} style={inputStyle} placeholder="Firmenname" /></div>
                        <div><label className="form-label">Ansprechpartner</label><input value={form.contact} onChange={f('contact')} style={inputStyle} placeholder="Name" /></div>
                        <div><label className="form-label">E-Mail</label><input type="email" value={form.email} onChange={f('email')} style={inputStyle} placeholder="email@example.com" /></div>
                        <div><label className="form-label">Produkt / Nische</label><input value={form.product_niche} onChange={f('product_niche')} style={inputStyle} placeholder="z.B. SaaS" /></div>
                        <div>
                            <label className="form-label">Status</label>
                            <select value={form.status} onChange={f('status')} style={selectStyle}>
                                {['Neu', 'Kontaktiert', 'Angebot', 'Gewonnen', 'Verloren'].map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="form-label">Priorität</label>
                            <select value={form.priority} onChange={f('priority')} style={selectStyle}>
                                {['Hoch', 'Mittel', 'Niedrig'].map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}><label className="form-label">Notizen <span style={{ color: MUTED, fontWeight: 400 }}>(Optional)</span></label><input value={form.notes} onChange={f('notes')} style={inputStyle} placeholder="Optional" /></div>
                        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px' }}>
                            <button type="submit" className="btn-primary">Speichern</button>
                            <CancelBtn onClick={() => setShowForm(false)} />
                        </div>
                    </form>
                </div>
            )}

            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            {['#', 'Firma', 'Kontakt', 'E-Mail', 'Nische', 'Status', 'Priorität', 'Notizen', ''].map(h => (
                                <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 700, color: MUTED, whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {leads.map((l, i) => (
                            <tr key={l.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ padding: '14px 16px', fontSize: '12px', color: MUTED }}>{i + 1}</td>
                                <td style={{ padding: '14px 16px', fontSize: '15px', fontWeight: 600 }}>{l.company || '—'}</td>
                                <td style={{ padding: '14px 16px', fontSize: '14px' }}>{l.contact || '—'}</td>
                                <td style={{ padding: '14px 16px', fontSize: '13px', color: MUTED }}>{l.email || '—'}</td>
                                <td style={{ padding: '14px 16px', fontSize: '13px', color: MUTED }}>{l.product_niche || '—'}</td>
                                <td style={{ padding: '14px 16px' }}>
                                    <select value={l.status} onChange={e => updateStatus(l.id, e.target.value)}
                                        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', padding: '7px 10px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                        {['Neu', 'Kontaktiert', 'Angebot', 'Gewonnen', 'Verloren'].map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td style={{ padding: '14px 16px' }}><Badge status={l.priority} /></td>
                                <td style={{ padding: '14px 16px', fontSize: '13px', color: MUTED, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.notes || '—'}</td>
                                <td style={{ padding: '14px 16px' }}>
                                    <button onClick={() => del(l.id, l.company)} className="icon-button" style={{ color: '#ef4444' }}><Trash2 size={15} /></button>
                                </td>
                            </tr>
                        ))}
                        {leads.length === 0 && <tr><td colSpan={9} style={{ padding: '48px', textAlign: 'center', color: MUTED }}>Noch keine Leads</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── MAIN ──────────────────────────────────────────────────────────────────────
export default function Essentials() {
    const { user } = useAuth();
    const token = user?.token;
    const isMobile = useWindowWidth() < 768;
    const [view, setView] = useState(() => localStorage.getItem(PENDING_RECHNER_KEY) ? 'invoices' : 'dashboard');

    const [clients, setClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [events, setEvents] = useState([]);

    const [rechnungen, setRechnungen] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [offers, setOffers] = useState([]);

    const fetchAll = useCallback(async () => {
        const h = { Authorization: `Bearer ${token}` };
        try {
            const [c, p, inv, ev, re, ct, of] = await Promise.all([
                fetch(`${API}/api/essentials/clients`, { headers: h }).then(r => r.ok ? r.json() : []),
                fetch(`${API}/api/essentials/projects`, { headers: h }).then(r => r.ok ? r.json() : []),
                fetch(`${API}/api/essentials/invoices`, { headers: h }).then(r => r.ok ? r.json() : []),
                fetch(`${API}/api/essentials/events`, { headers: h }).then(r => r.ok ? r.json() : []),
                fetch(`${API}/api/rechnungen`, { headers: h }).then(r => r.ok ? r.json() : []),
                fetch(`${API}/api/essentials/contracts`, { headers: h }).then(r => r.ok ? r.json() : []),
                fetch(`${API}/api/essentials/offers`, { headers: h }).then(r => r.ok ? r.json() : []),
            ]);
            setClients(Array.isArray(c) ? c : []);
            setProjects(Array.isArray(p) ? p : []);
            setInvoices(Array.isArray(inv) ? inv : []);
            setEvents(Array.isArray(ev) ? ev : []);
            setRechnungen(Array.isArray(re) ? re : []);
            setContracts(Array.isArray(ct) ? ct : []);
            setOffers(Array.isArray(of) ? of : []);
        } catch (_) { }
    }, [token]);

    useEffect(() => { fetchAll(); }, [fetchAll]);
    useAutoRefresh(fetchAll, 15000);

    const sharedProps = { token, clients, projects, invoices, events, rechnungen, contracts, offers, onRefresh: fetchAll, isMobile };

    return (
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', minHeight: 'calc(100vh - 70px)' }}>
            <Sidebar view={view} setView={setView} isMobile={isMobile} />
            <main style={{ flex: 1, padding: isMobile ? '24px 16px' : '40px 48px', overflowX: 'hidden', overflowY: 'auto' }}>
                <motion.div key={view} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
                    {view === 'dashboard' && <Dashboard {...sharedProps} setView={setView} />}
                    {view === 'projects' && <ProjectsView {...sharedProps} />}
                    {view === 'clients' && <ClientsView {...sharedProps} />}
                    {view === 'invoices' && <InvoicesView {...sharedProps} />}
                    {view === 'offers' && <OffersView {...sharedProps} />}
                    {view === 'contracts' && <ContractsView {...sharedProps} />}
                    {view === 'calendar' && <CalendarView {...sharedProps} />}
                    {view === 'financials' && <FinancialsView {...sharedProps} />}
                </motion.div>
            </main>
        </div>
    );
}
